import { inject, Injectable } from '@angular/core';
import { delay, distinctUntilChanged, skip, tap } from 'rxjs/operators';
import { Observable, ReplaySubject } from 'rxjs';
import { LanguageService } from '../language.service';

type ContentUpdaterItem = {
  value: ReplaySubject<any>;
  updateFn: () => Observable<any>;
  updated?: boolean;
}

/**
 * Uses for UpdateOnLangChange decorator
 * Updates value on lang change
 */
@Injectable({
  providedIn: 'root',
})
export class ContentUpdaterService {

  private _lang = inject(LanguageService);

  /**
   * Storage for content emitters
   */
  private _storage: Map<string, ContentUpdaterItem> = new Map<string, ContentUpdaterItem>();

  registerContent(key: string, params: any = {}, updateFn: () => Observable<any>): Observable<any> {
    const fullKey = key + JSON.stringify(params);
    const item: ContentUpdaterItem = {
      value: new ReplaySubject<any>(1),
      updateFn,
    };
    return this.addItem(fullKey, item);
  }

  /**
   * Add new item to storage
   *
   * @param key
   * @param item
   */
  addItem(key: string, item: ContentUpdaterItem) {
    if (!this._storage.has(key)) {
      this._storage.set(key, item);
    }

    const addedItem: any = this._storage.get(key);

    if (!addedItem.updated) {
      addedItem.value.next(undefined);
    }

    return addedItem.value.pipe(
      tap(val => {
        if (!addedItem.updated && val !== null) {
          this._updateItemByKey(key);
        }
      }),
    );
  }

  /**
   * Update all items that has active subscribers
   */
  updateAll() {
    this._storage.forEach((item: ContentUpdaterItem, key: string) => {
      if (this._hasActiveSubscribers(item.value)) {
        this._updateItemByKey(key);
      } else {
        item.updated = false;
      }
    });
  }

  /**
   * Update item value in storage
   *
   * @param key
   * @private
   */
  private _updateItemByKey(key: string) {
    const item: any = this._storage.get(key);

    item.value.next(null);
    item.updateFn().pipe(
      /**
       * Needs for templates re-rendering
       */
      delay(1),
    ).subscribe((response: any) => {
      item.updated = true;
      item.value.next(response);
    });
  }

  /**
   * Handles content updates when the language changes
   * Subscribes to language change events and triggers an update of all content
   */
  public runContentChangeHandler() {
    this._lang.langChange$.pipe(
      distinctUntilChanged(),
      skip(1),
      tap(lang => {
        this.updateAll();
      }),
    ).subscribe();
  }

  /**
   * Returns true if provided observable has active subscribers
   *
   * @param observable
   * @private
   */
  private _hasActiveSubscribers(observable: any): boolean {
    // tslint:disable:no-string-literal
    return (
      observable.observers.length ||
      observable['destination'] &&
      observable['destination']['observers'] &&
      observable['destination']['observers'].length
    );
    // tslint:enable:no-string-literal
  }
}
